/**
 * CRDT schema v2 stream projection (LG-2847) — full parse.
 *
 * Pure function turning the normalized v2 `doc["t"]` stream into the ordered `DerivedParagraph`
 * model the UI needs (see `derived.ts`). Timings and character offsets are intentionally excluded —
 * those are joined/recomputed by LG-2848, which extends this output.
 *
 * The core operates on a normalized delta (`StreamDelta`) rather than a live `Y.XmlText`, so it never
 * imports Yjs and every test is a plain array. The adapter that produces this delta from the live doc
 * (XmlFragment → XmlText → `toDelta()`, live `Map` embeds → `toJSON()`) belongs to LG-2850.
 */

import { Marker, UtteranceChangeMarker } from './markers'
import { DerivedParagraph, DerivedRun } from './derived'
import { isParagraphChange, isUtteranceChange } from './guards'

/**
 * A single op of the normalized stream: either a plain text run or a recognized marker embed. This is
 * what LG-2850's adapter emits from `doc["t"]`. Consecutive text inserts are concatenated by the walk;
 * unrecognized embeds are treated as opaque and skipped.
 */
export type StreamDeltaOp = { insert: string } | { insert: Marker }

/**
 * The full v2 `doc["t"]` stream as a normalized delta.
 */
export type StreamDelta = StreamDeltaOp[]

/**
 * Project the v2 `doc["t"]` stream into ordered derived paragraphs (no timings/char offsets — LG-2848).
 *
 * Single linear walk maintaining running state: the current paragraph, the current utterance (carried
 * across paragraph boundaries so a spanning utterance continues into the next paragraph), and a text
 * buffer. The buffer is flushed to one `DerivedRun` at every marker boundary and at end-of-stream.
 *
 * Segmentation & carry:
 * - `paragraph_change` starts a new paragraph (carrying `speakerId` and `qac`, which defaults to
 *   `null`); the current utterance is preserved so its text continues into the new paragraph.
 * - `utterance_change` starts a new run; its `id` + `final` govern the following run(s).
 * - `audit_event` embeds are skipped and never split a run (emission is deferred — design decision 7).
 *
 * Run ↔ utterance contract (relied on by LG-2848/LG-2849/LG-2850): one run is emitted per utterance-segment, and each
 * run carries its own `utteranceId` — the utterance whose words it contains — so LG-2848 can join per-word
 * timings by position within that utterance without re-walking the raw stream and without depending on
 * any positional parallelism between separate arrays. `run.finalized` is that utterance's `final` flag.
 * Every paragraph has at least one run (see the empty-paragraph rule below).
 *
 * Edge cases (per spec):
 * - Text with no active utterance (words before any `utterance_change`) is preserved as a single
 *   `finalized: true` run with `utteranceId: null` (no timing anchor).
 * - A zero-length utterance-segment *within* a paragraph that has other content (e.g. adjacent
 *   `utterance_change` markers) emits no run.
 * - An empty paragraph — one that produces no content runs at all (Enter at a paragraph edge, or all
 *   text erased; a valid, editable state) — is represented by a single empty placeholder run
 *   (`text: ''`) derived from the closest `utterance_change` above it, exactly like a normal run: its
 *   `utteranceId`/`finalized` come from that utterance (`null`/`true` when none precedes). So `runs`
 *   is never empty. It still has no words, so no derived timestamp regardless of its `utteranceId`.
 * - Text before the first `paragraph_change` has no paragraph to attach to and is dropped.
 *
 * Pure — never mutates its input or any document.
 *
 * `initialUtterance` seeds the walk's current utterance (LG-2849 window re-project). Omitted or
 * `null` matches a walk that has not yet seen an `utterance_change`.
 */
export const projectStream = (delta: StreamDelta, initialUtterance: UtteranceChangeMarker | null = null): DerivedParagraph[] => {
    const paragraphs: DerivedParagraph[] = []
    let current: DerivedParagraph | null = null
    let currentUtterance: UtteranceChangeMarker | null = initialUtterance
    let buffer = ''

    // Build a run in the current utterance context: `finalized` and `utteranceId` come from the closest
    // `utterance_change` above (the carried current utterance), or `true`/`null` when none precedes.
    const runFrom = (text: string): DerivedRun => ({
        text,
        finalized: currentUtterance ? currentUtterance.final : true,
        utteranceId: currentUtterance ? currentUtterance.id : null,
    })

    const flushRun = () => {
        // Nothing to emit: no paragraph yet (text before the first `paragraph_change` is dropped) or an
        // empty segment. Reset the buffer either way so dropped leading text can't leak into a paragraph.
        if (current === null || buffer.length === 0) {
            buffer = ''

            return
        }

        current.runs.push(runFrom(buffer))
        buffer = ''
    }

    // Close the current paragraph: flush any pending text, then guarantee it has at least one run. An
    // empty paragraph (no content runs — e.g. Enter at a paragraph edge, or all text erased) is a valid,
    // editable state, represented by a single empty placeholder run derived like any other run from the
    // closest `utterance_change` above it (same as non-empty paragraphs). It still has no words, so no
    // derived timestamp regardless of which utterance it anchors to.
    const closeParagraph = () => {
        flushRun()
        if (current !== null && current.runs.length === 0) {
            current.runs.push(runFrom(''))
        }
    }

    for (const op of delta) {
        const { insert } = op

        if (typeof insert === 'string') {
            buffer += insert
            continue
        }

        if (isParagraphChange(insert)) {
            closeParagraph()
            current = {
                id: insert.id,
                speakerId: insert.speakerId,
                qac: insert.qac ?? null,
                runs: [],
            }
            paragraphs.push(current)
            continue
        }

        if (isUtteranceChange(insert)) {
            flushRun()
            currentUtterance = insert
            continue
        }

        // Any embed LG-2847 doesn't act on — `audit_event` today, or an unknown/forward-compatible marker
        // kind a future schema adds — is skipped without flushing, so it never splits a run. Note such
        // embeds still occupy one unit of `XmlText` index space: LG-2847 is offset-free, but LG-2849's absolute
        // offset→paragraph mapping must count them (see the tech design's "Incremental updates").
    }

    closeParagraph()

    return paragraphs
}
